<script lang="ts">
    let id = 0;
</script>
<script setup lang="ts">
    import { RuleExpression, useField, useFieldError } from 'vee-validate';
    import { Field, FieldLabel, FieldDescription, FieldError } from '../../../ui/Field';
    import { InputGroup, InputGroupTextarea } from '../../../ui/InputGroup';
    import { required } from '@vee-validate/rules';
    import { TooltipButton } from '@/shared/ui/TooltipButton';
    import { Collapsible, CollapsibleTrigger } from '@/shared/ui/Collapsible';
    import { FieldHint } from '@/shared/ui/FieldHint';

    const props = withDefaults(
        defineProps<{
            name?: string;
            label?: string;
            placeholder?: string;
            description?: string;
            loading?: boolean;
            tooltip?: string;
            /** Rules will be ignored if you use schema based validation */
            rules?: MaybeRef<RuleExpression<any>>;
            disabled?: boolean;
            hideError?: boolean;
        }>(),
        {
            name: () => `textarea_field_${++id}`,
        },
    );

    const model = defineModel<string>();
    const { value: localModel, meta } = useField(props.name, props.rules, { initialValue: model.value });
    const error = useFieldError(props.name);

    const isInvalid = computed(() => meta.touched && !!error.value);

    const isRequired = computed(() => {
        if (typeof props.rules === 'string') {
            return !!props.rules.includes('required');
        }

        if (typeof props.rules === 'object') {
            return Object.values(props.rules).some((validator) => validator === required);
        }

        if (Array.isArray(props.rules)) {
            return props.rules.some((validator) => validator === required);
        }

        return false;
    });

    watch(localModel, (value) => {
        if (value === model.value) return;
        model.value = value;
    });
    watch(model, (modelValue) => {
        if (modelValue === localModel.value) return;
        localModel.value = modelValue;
    });
</script>

<template>
    <Collapsible>
        <Field>
            <div
                v-if="label || $slots.label"
                class="flex gap-1 items-center"
            >
                <FieldLabel>
                    <slot name="label">{{ label }}</slot>
                </FieldLabel>
                <CollapsibleTrigger as-child>
                    <TooltipButton v-if="$slots.tooltip || tooltip"></TooltipButton>
                </CollapsibleTrigger>
            </div>

            <FieldHint v-if="$slots.tooltip || tooltip">
                <slot name="tooltip">{{ tooltip }}</slot>
            </FieldHint>

            <InputGroup>
                <InputGroupTextarea
                    v-model="localModel"
                    :name="name"
                    :placeholder="placeholder"
                    :disabled="disabled || loading"
                    :aria-invalid="isInvalid"
                    :aria-required="isRequired"
                />
            </InputGroup>

            <FieldDescription v-if="description || $slots.description">
                <slot name="description">{{ description }}</slot>
            </FieldDescription>

            <FieldError v-if="isInvalid && !hideError">{{ error }}</FieldError>
        </Field>
    </Collapsible>
</template>
